import config from '../config';
import _ from 'lodash';
import logger from './logger';
import constants from './constants';
import tcAnalytics from './analytics';
import threeDSHelper from './threeDS-util';
import urlHelper from './url';
import loader from './loader';
import egtnlUtil from './egtnl-util';

/**
 * Get the appropriate 3DS connector URL based on the experiment
 * @returns {string} The connector URL to use
 */
const getThreeDSConnectorUrl = () => {
    const baseConfig = config?.threeDSConfig?.config;
    if (egtnlUtil.is3DSV2UpgradeEnabled() && baseConfig.threeDSConnectorUrlV2) {
        return baseConfig.threeDSConnectorUrlV2;
    }
    return baseConfig.threeDSConnectorUrl;
};

/*
 * threeDSFrame is a connector to PaymentJS library provided by Expedia to facilitate interactions with the 3DS challenge window
 * */
const threeDSFrame = {
    properties: config.threeDSConfig.config,

    /**
     * Get the connector URL (supports experiment-based version switching)
     */
    getConnectorUrl() {
        return getThreeDSConnectorUrl();
    },

    enabled: true,

    challengeContainer: null,

    model: null,

    challengeResponseStatusCode: null,

    bindIFrameContainer(container) {
        this.challengeContainer = container;
    },

    isEnabled() {
        return !_.isEmpty(this.challengeContainer);
    },

    async setup() {
        if (this.isEnabled()) {
            this.ifr =
                this.ifr ||
                // eslint-disable-next-line no-undef
                new PayThreeDSConnector.ThreeDSConnector(
                    this.properties.threeDSFrameId,
                    this.properties.threeDSFrameSrc
                );
            try {
                if (!_.isEmpty(this.ifr)) {
                    const res = await this.ifr.setup({referenceId: '123'});
                    return res.encodedBrowserMetadata
                        ? res.encodedBrowserMetadata
                        : false;
                }
                logger.log(
                    `threeds:setup skipping setup due to empty(uninitialized) iframe ${this.ifr}`
                );
            } catch (e) {
                logger.log(`threeds:setup failed with ${e}`);
            }
        }
        return false;
    },

    async initSession(model) {
        if (
            this.isEnabled() &&
            !_.isEmpty(this.ifr) &&
            !_.isEmpty(model.paymentSessionId)
        ) {
            tcAnalytics.genericEvent(
                'Checkout is 3DS eligible',
                constants.EDAP_CATEGORIES.THREE_DS
            );
            if (!_.isEmpty(model.encodedInitConfig)) {
                try {
                    const res = await this.ifr.initSession({
                        paymentSessionId: model.paymentSessionId,
                        encodedInitConfig: model.encodedInitConfig,
                    });
                    logger.log(`threeds::initSession returned ${res}`);
                } catch (e) {
                    logger.log(`threeds:initSession failed with ${e}`);
                }
            }
        }
    },

    async cleanUpBooking() {
        if (!_.isEmpty(this.model)) {
            // only call resume if back button or cancel payment was pressed
            if (_.isEmpty(this.challengeResponseStatusCode)) {
                logger.log(`back button or cancel payment click detected while showing 3ds challenge window, calling resumeCheckout for
                threeDSRecordLocatorId: ${this.model.threeDSRecordLocatorId}`);

                await threeDSHelper.resumeCheckout({
                    payload: this.model.payload,
                });
            }
            // if vasInterstitial
            if (this.model.isVasInterstitial) {
                // redirect to confirmation page
                window.location.href = urlHelper.createConfirmationUrl(
                    this.model.data
                );
            }
            this.challengeContainer.hide();
            loader.hideLoading('body');
        }
    },

    async challenge(model) {
        if (
            this.isEnabled() &&
            !_.isEmpty(this.ifr) &&
            !_.isEmpty(model.paymentSessionId) &&
            !_.isEmpty(model.encodedChallengeConfig)
        ) {
            try {
                tcAnalytics.genericEvent(
                    'Checkout is 3DS challenged',
                    constants.EDAP_CATEGORIES.THREE_DS
                );
                // save model to threeDSFrame so if user clicks back button during a challenge, we can cleanUpBooking
                // TODO: Find a better identifier for AOE vs CS
                if (model.threeDSRecordLocatorId) {
                    this.model = {
                        orderNumber: model.threeDSRecordLocatorId,
                        threeDSRecordLocatorId: model.threeDSRecordLocatorId,
                        site: model.reservation.travelerSite,
                        currency: model.currencyCode,
                        data: model.data,
                    };
                } else {
                    this.model = model;
                }

                this.challengeContainer.show();
                logger.log(`presenting threeds:challenge for paymentSessionId ${model.paymentSessionId}
                    and encodedChallengeConfig ${model.encodedChallengeConfig}`);
                const challengeResponse = await this.ifr.challenge({
                    paymentSessionId: model.paymentSessionId,
                    encodedChallengeConfig: model.encodedChallengeConfig,
                });
                if (
                    !_.isUndefined(challengeResponse) &&
                    !_.isUndefined(challengeResponse.statusCode)
                ) {
                    tcAnalytics.genericEvent(
                        `Checkout 3DS challenge result: ${challengeResponse.message}`,
                        constants.EDAP_CATEGORIES.THREE_DS
                    );
                    model.threeDSChallengeStatus = challengeResponse.statusCode;
                    this.challengeResponseStatusCode =
                        challengeResponse.statusCode;
                    if (challengeResponse.statusCode === 'SUCCESS') {
                        logger.log(`successful threeds::challenge for paymentSessionId ${model.paymentSessionId}
                            and encodedChallengeConfig ${model.encodedChallengeConfig}`);
                    } else if (challengeResponse.statusCode === 'FAILED') {
                        logger.log(`threeds::challenge failed, repeating for paymentSessionId ${model.paymentSessionId}
                            and encodedChallengeConfig ${model.encodedChallengeConfig}`);
                    } else if (challengeResponse.statusCode === 'ERROR') {
                        logger.log(`threeds::challenge completed with status ${challengeResponse.statusCode} and error ${challengeResponse.message}
                            for paymentSessionId ${model.paymentSessionId}
                            and encodedChallengeConfig ${model.encodedChallengeConfig}`);
                    } else if (challengeResponse.statusCode === 'TIMEOUT') {
                        logger.log(`threeds::challenge timed out with status ${challengeResponse.statusCode} and error ${challengeResponse.message}
                            for paymentSessionId ${model.paymentSessionId}
                            and encodedChallengeConfig ${model.encodedChallengeConfig}`);
                        model.threeDSChallengeStatus = 'ERROR';
                    }
                } else {
                    logger.log(`challenge completed with unknown status: ${challengeResponse} for paymentSessionId ${model.paymentSessionId}
                            and encodedChallengeConfig ${model.encodedChallengeConfig}`);
                    model.threeDSChallengeStatus = 'ERROR';
                    this.challengeResponseStatusCode = 'ERROR';
                }
            } catch (e) {
                logger.log(`threeds:challenge failed with ${e} for paymentSessionId ${model.paymentSessionId}
                            and encodedChallengeConfig ${model.encodedChallengeConfig}`);
                model.threeDSChallengeStatus = 'ERROR';
                this.challengeResponseStatusCode = 'ERROR';
            } finally {
                this.challengeContainer.hide();
            }
        }
    },
};

export default threeDSFrame;
